import { PDF_SIZE } from '@/constant';
import { ExportableResultSlide } from '@/pages/Results/components/ExportableResultSlide';
import { dataDisplayCustomizations } from '@/themes/customizations/dataDisplay';
import { feedbackCustomizations } from '@/themes/customizations/feedback';
import { inputsCustomizations } from '@/themes/customizations/inputs';
import { navigationCustomizations } from '@/themes/customizations/navigation';
import { surfacesCustomizations } from '@/themes/customizations/surfaces';
import { colorSchemes, shadows, shape, typography } from '@/themes/themePrimitives';
import { Question } from '@/types/slide';
import { Box, Grid, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import { jsPDF } from 'jspdf';
import { domToDataUrl } from 'modern-screenshot';
import React, { forwardRef, useMemo, useRef, type ReactNode } from 'react';
import { createRoot, Root } from 'react-dom/client';

interface PDFData {
    name: string;
    theme: string;
    class: string;
    targetLanguageFeatures: string;
}
export interface PDFOptions {
    title?: string;
    data?: PDFData;
    orientation?: 'portrait' | 'landscape';
    format?: 'a1' | 'a2' | 'a3' | 'a4' | 'a5' | 'a6' | 'a7';
    answersPerPage?: 1 | 2 | 3 | 4;
    scale?: number;
    quality?: number;
    margins?: {
        top: number;
        right: number;
        bottom: number;
        left: number;
    };
}

const FieldContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
});

const FieldLabel = styled(Typography)(({ theme }) => ({
    color: '#555',
    marginRight: theme.spacing(1),
    paddingBottom: 1,
}));

const UnderlinedField = styled(Box)(() => ({
    borderBottom: '2px solid #aaa',
    color: '#333',
    minHeight: '24px',
    flexGrow: 1,
}));

const ScaledContent: React.FC<{ children: ReactNode; scale: number }> = ({ children, scale }) => {
    const frameContentAreaRef = useRef<HTMLDivElement | null>(null);
    const measuredContentRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            ref={frameContentAreaRef}
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div
                ref={measuredContentRef}
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%) scale(${scale})`,
                    transformOrigin: 'center',
                    willChange: 'transform',
                }}
            >
                {children}
            </div>
        </div>
    );
};

type FrameProps = {
    title: string;
    data: PDFData;
    answersPerPage: 1 | 2 | 3 | 4;
    children: ReactNode[];
};

const Frame = forwardRef<HTMLDivElement, FrameProps>(({ title, data, answersPerPage, children }, ref) => {
    return (
        <div
            ref={ref}
            style={{
                width: PDF_SIZE.width,
                height: PDF_SIZE.height,
                color: '#555',
                background: '#fff',
                boxSizing: 'border-box',
                padding: '40px 30px',
                fontFamily: 'Arial, sans-serif',
                position: 'relative',
                border: '1px solid #ccc',
            }}
        >
            <Typography variant='h5' sx={{ textAlign: 'center', marginBottom: '50px' }}>
                {title}
            </Typography>

            <Box
                sx={{
                    width: '100%',
                    mx: 'auto',
                    alignSelf: 'center',
                }}
            >
                <Grid container spacing={2} sx={{ height: '100%' }}>
                    <Grid size={4}>
                        <FieldContainer>
                            <FieldLabel>Name:</FieldLabel>
                            <UnderlinedField>{data.name}</UnderlinedField>
                        </FieldContainer>
                    </Grid>
                    <Grid size={4}>
                        <FieldContainer>
                            <FieldLabel>Theme:</FieldLabel>
                            <UnderlinedField>{data.theme}</UnderlinedField>
                        </FieldContainer>
                    </Grid>
                    <Grid size={4}>
                        <FieldContainer>
                            <FieldLabel>Class:</FieldLabel>
                            <UnderlinedField>{data.class}</UnderlinedField>
                        </FieldContainer>
                    </Grid>
                    <Grid size={12}>
                        <FieldContainer>
                            <FieldLabel>Target Language Features:</FieldLabel>
                            <UnderlinedField>{data.targetLanguageFeatures}</UnderlinedField>
                        </FieldContainer>
                    </Grid>
                </Grid>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    height: '580px',
                    mx: 'auto',
                    mt: 2,
                    p: 2,
                    alignSelf: 'center',
                }}
            >
                {/* note: answersPerPage in keys to prevent reusing previous component due to identical key*/}
                {/* use hard coded scale since its fixed size anyway */}
                <Grid container spacing={2} sx={{ height: '100%' }}>
                    {/* renders normally if answersPerPage == 1 or 2 */}
                    {(answersPerPage == 1 || answersPerPage == 2) &&
                        children.map((child, index) => (
                            <Grid key={`${answersPerPage}_${index}`} size={answersPerPage == 1 ? 12 : 6}>
                                <ScaledContent scale={answersPerPage == 1 ? 1 : 0.79140625}>{child}</ScaledContent>
                            </Grid>
                        ))}
                    {/* always pad to 4 items if answersPerPage == 3 or 4 */}
                    {(answersPerPage == 3 || answersPerPage == 4) &&
                        children.concat(Array(4 - children.length).fill(null)).map((child, index) => (
                            <Grid key={`${answersPerPage}_${index}`} size={6}>
                                {child && <ScaledContent scale={0.5541666666666667}>{child}</ScaledContent>}
                            </Grid>
                        ))}
                </Grid>
            </Box>
        </div>
    );
});

export interface PdfFrameProps {
    title: string;
    data: {
        name: string;
        theme: string;
        class: string;
        targetLanguageFeatures: string;
    };
    answersPerPage: 1 | 2 | 3 | 4;
    theme: Theme;
    children: React.ReactNode[];
}

export const PdfFrame: React.FC<PdfFrameProps> = ({ title, data, answersPerPage, theme, children }) => {
    return (
        <ThemeProvider theme={theme}>
            <div style={{ display: 'block' }}>
                <Frame title={title} data={data} answersPerPage={answersPerPage}>
                    {children}
                </Frame>
            </div>
        </ThemeProvider>
    );
};

export const renderPdfFrame = async (slides: Question[], root: Root, options: PDFOptions, theme: Theme) => {
    const frameData = options.data ?? {
        name: 'Name',
        theme: 'Theme',
        class: '',
        targetLanguageFeatures: '',
    };

    const promises: Promise<void>[] = [];

    const children = slides.map((slide, index) => {
        let onDone: () => void = () => {};
        const promise = new Promise<void>((resolve) => {
            onDone = resolve;
        });
        promises.push(promise);
        return <ExportableResultSlide key={index} question={slide} onLoad={() => onDone()} />;
    });

    root.render(
        <PdfFrame theme={theme} title={options.title ?? 'Document'} data={frameData} answersPerPage={options.answersPerPage ?? 1}>
            {children}
        </PdfFrame>,
    );

    await Promise.all(promises);

    return root;
};

const generatePdf = async (slides: Question[], options: PDFOptions = {}, theme: Theme): Promise<jsPDF> => {
    const { orientation = 'landscape', format = 'a4', scale = 2, quality = 1 } = options;

    // Create a temporary container
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '0px';
    container.style.top = '0px';

    container.style.pointerEvents = 'none';

    container.style.width = `${PDF_SIZE.width}px`;
    container.style.height = `${PDF_SIZE.height}px`;
    container.style.backgroundColor = 'white';

    container.style.zIndex = '-99999'; // hide the frame under other components
    document.body.appendChild(container);

    try {
        const pdf = new jsPDF({
            orientation,
            unit: 'mm',
            format,
            compress: true,
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const answersPerPage = options.answersPerPage ?? 1;
        const numberOfSlides = slides.length;

        const root = createRoot(container);

        for (let currentSlideIdx = 0; currentSlideIdx < numberOfSlides; currentSlideIdx = currentSlideIdx + answersPerPage) {
            if (currentSlideIdx > 0) {
                pdf.addPage();
            }

            await renderPdfFrame(slides.slice(currentSlideIdx, currentSlideIdx + answersPerPage), root, options, theme);

            const dataUrl = await domToDataUrl(container, {
                scale,
                quality,
                fetch: {
                    requestInit: {
                        cache: 'no-cache',
                    },
                },
            });

            pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight, '', 'FAST');
        }

        return pdf;
    } catch (error) {
        console.error('Error generating PDF:', error);
        throw error;
    } finally {
        // Clean up
        if (document.body.contains(container)) {
            document.body.removeChild(container);
        }
    }
};

export const usePdfGenerator = () => {
    // copied from theme.ts
    // cssVariables is removed as they seem to affect the main app's theme when applied
    const theme: Theme = useMemo(() => {
        return createTheme({
            colorSchemes,
            typography,
            shadows,
            shape,
            components: {
                ...inputsCustomizations,
                ...dataDisplayCustomizations,
                ...feedbackCustomizations,
                ...navigationCustomizations,
                ...surfacesCustomizations,
            },
        });
    }, []);

    const generate = async (slides: Question[], options: PDFOptions = {}) => {
        return await generatePdf(slides, options, theme);
    };

    return { generatePdf: generate };
};
